import React, { useState, ReactNode, useEffect, useRef } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import expanderIc from '@/assets/icons/expander.svg';
import cornerDecorator from '@/assets/icons/cornerDecorator.svg';
import { SidebarItem } from './sidebarData';
import styles from './Sidebar.module.css';
import useFundContext from '@/contexts/Fund/useFundContext';
import { useSidebarBadges } from '@/contexts/SidebarBadge/SidebarBadgeProvider';
import useFeatureFlags from '@/contexts/FeatureFlags/useFeatureFlags';
import { useAnnouncements } from '@/components/Announcements/AnnouncementsContext';
import NavBadge from './NavBadge';

interface NavItemProps {
  to?: string;
  children?: ReactNode;
  sideBarItem: SidebarItem;
  isCollapsed?: boolean;
  /** The sidebar is an icon rail: labels are hidden and sub-menus have nowhere to open. */
  isRail?: boolean;
  /** Called when a rail item needs the sidebar back at full width to be usable. */
  onRequestExpand?: () => void;
}

const enabledRoutes: string[] = import.meta.env.VITE_APP_ENABLED_ROUTES;

const NavItem: React.FC<NavItemProps> = ({
  to,
  children,
  sideBarItem,
  isCollapsed = false,
  isRail = false,
  onRequestExpand,
}) => {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);
  const isExpandable = Boolean(children);
  const { currentFund } = useFundContext();
  const location = useLocation();
  const [hasActiveChild, setHasActiveChild] = useState(false);
  const autoExpandedRef = useRef(false);
  const badges = useSidebarBadges();
  const badgeCount = sideBarItem.badgeKey ? (badges[sideBarItem.badgeKey] || 0) : 0;

  // Check if any child routes are active
  useEffect(() => {
    if (sideBarItem.children && currentFund) {
      const isAnyChildActive = sideBarItem.children.some((child) => {
        const childPath = `/${currentFund.id}/${child.to}`;
        return location.pathname.startsWith(childPath);
      });
      
      setHasActiveChild(isAnyChildActive);

      if (isAnyChildActive && !isCollapsed && !autoExpandedRef.current) {
        setIsExpanded(true);
        autoExpandedRef.current = true;
      }
      
      if (!isAnyChildActive) {
        autoExpandedRef.current = false;
      }
    }
  }, [sideBarItem.children, location.pathname, currentFund, isCollapsed]);

  const { isEnabled } = useFeatureFlags();

  const isNavItemEnabled = (navItem: SidebarItem): boolean => {
    // A feature-flagged item is controlled SOLELY by its flag (the flag is the single source of
    // truth for rollout). It stays visible but blurred + non-clickable while the flag is off, and
    // is revealed the moment an admin releases it from the management screen — at every site,
    // regardless of the static VITE_APP_ENABLED_ROUTES list (which still gates non-flagged items).
    if (navItem.featureKey) return isEnabled(navItem.featureKey) || !!navItem.alsoEnabledBy?.some((key) => isEnabled(key));
    if (navItem.children) return navItem.children.some((child) => isNavItemEnabled(child));
    return navItem.to ? enabledRoutes.includes(navItem.to) : false;
  };

  const isRouteEnabled = isNavItemEnabled(sideBarItem);

  /*
   * «חדש» while the screen's announcement is unread (HAT-1489). Only on an item the user can open:
   * a blurred item marked new would be a promise the menu cannot keep. Visiting the screen reads the
   * announcement, and the chip goes (see AnnouncementsProvider).
   */
  const announcements = useAnnouncements();
  // A group shows «חדש» when one of the screens under it that this user can open is new.
  const openFeatureKeysOf = (item: SidebarItem): (string | undefined)[] => (isNavItemEnabled(item)
    ? [item.featureKey, ...(item.children ?? []).flatMap(openFeatureKeysOf)]
    : []);
  const isNew = isRouteEnabled && !!announcements?.hasUnread(openFeatureKeysOf(sideBarItem), 'fund');

  const handleExpandClick = () => {
    if (!isExpandable) return;
    // A sub-menu has nowhere to unfold inside a 76px rail, so opening one is
    // also a request to open the menu.
    if (isRail) {
      onRequestExpand?.();
      setIsExpanded(true);
    } else {
      setIsExpanded(!isExpanded);
    }
    autoExpandedRef.current = true;
  };

  // Without a label beside it an icon has to name itself somehow.
  const railTitle = isRail ? t(sideBarItem.text) : undefined;

  // An open group shows its children, and the child carries the count - once is enough
  const showsBadge = badgeCount > 0 && !(isExpandable && isExpanded && !isCollapsed && !isRail);
  const countBadge = showsBadge ? (
    <NavBadge
      count={badgeCount}
      className={styles.navBadge}
      iconicClassName={styles.navBadgeIconic}
      icon={sideBarItem.badgeIcon}
      tooltipKey={sideBarItem.badgeTooltip}
      // A closed group only says that something waits inside; the item says what and how many
      asDot={isExpandable}
      dotClassName={styles.navBadgeDot}
    />
  ) : null;
  const badgeElement = (
    <>
      {countBadge}
      {isNew && <span className={styles.navNew}>{t('announcements.new')}</span>}
    </>
  );

  // Items without an icon are indented to the width of one, so every label in
  // the column starts on the same line.
  const labelClassName = `${styles.navLabel} ${sideBarItem.iconSrc ? '' : 'ps-[36px]'}`;

  /* הלשונית הלבנה של הפריט הפעיל נמשכת אל התוכן, ושני העיטורים משלימים את
     הפינות שמעליה ומתחתיה. ברייל אין לשונית, וה-CSS מסתיר אותם. */
  const getActiveContent = () => (
    <>
      <img src={cornerDecorator} className={styles.topCornerDecorator} alt="" aria-hidden="true" />
      {sideBarItem.activeIconSrc && <img src={sideBarItem.activeIconSrc} className={styles.navIcon} alt="" aria-hidden="true" />}
      <span className={labelClassName}>{t(sideBarItem.text)}</span>
      {badgeElement}
      {isExpandable && !isCollapsed && (
        <img
          src={expanderIc}
          alt=""
          className={isExpanded ? styles.expanderIconExpanded : styles.expanderIcon}
          aria-hidden="true"
        />
      )}
      <img src={cornerDecorator} className={styles.bottomCornerDecorator} alt="" aria-hidden="true" />
    </>
  );

  const getContent = () => (
    <>
      {sideBarItem.iconSrc && <img src={sideBarItem.iconSrc} className={styles.navIcon} alt="" aria-hidden="true" />}
      <span className={labelClassName}>{t(sideBarItem.text)}</span>
      {badgeElement}
      {isExpandable && (
        <img
          src={expanderIc}
          alt=""
          className={isExpanded ? styles.expanderIconExpanded : styles.expanderIcon}
          aria-hidden="true"
        />
      )}
    </>
  );

  if (isExpandable) {
    return (
      <>
        <button
          className={`${styles.navItem} ${styles.expandableNavItem} ${hasActiveChild && (isCollapsed || !isExpanded) ? styles.activeNavItem : ''} ${!isRouteEnabled ? styles.disabledNavItem : ''}`}
          onClick={handleExpandClick}
          aria-expanded={isExpanded}
          tabIndex={isRouteEnabled ? 0 : -1}
          title={railTitle}
        >
          {hasActiveChild && (isCollapsed || !isExpanded) ? getActiveContent() : getContent()}
        </button>

        {!isCollapsed && (
          <div className={`${styles.childrenContainer} ${isExpanded ? styles.childrenContainerExpanded : ''}`}>
            <div className={styles.childrenInner}>{children}</div>
          </div>
        )}
      </>
    );
  }

  if (!to) {
    return (
      <span className={`${styles.navItem} ${styles.disabledNavItem}`} aria-disabled="true">
        {getContent()}
      </span>
    );
  }

  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        `${styles.navItem} ${isActive ? styles.activeNavItem : styles.regularNavItem} ${!isRouteEnabled ? styles.disabledNavItem : ''}`
      }
      tabIndex={isRouteEnabled ? 0 : -1}
      title={railTitle}
    >
      {({ isActive }) => (isActive ? getActiveContent() : getContent())}
    </NavLink>
  );
};

export default NavItem;
